import React, { memo, useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import OiChartSkeletonPlaceholder from './OiChartSkeletonPlaceholder'

const CHART_SX = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    pointerEvents: 'none',
    bgcolor: 'background.paper',
} as const

const METADATA_OVERLAY_SX = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 6,
    pointerEvents: 'none',
} as const

/** Compact OI metadata skeleton — symbol row + single strike row. */
const OiMetadataSkeletonPlaceholder = () => {
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'
    const colors = useMemo(
        () => ({
            fill: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.09)',
            muted: isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.06)',
        }),
        [isDark]
    )

    return (
        <Box
            className="pane-meta-container"
            sx={{
                position: 'absolute',
                top: '4px',
                left: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 24 }}>
                <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: colors.muted }} />
                <Box sx={{ width: 80, height: 14, borderRadius: 0.5, bgcolor: colors.fill }} />
                <Box sx={{ width: 52, height: 14, borderRadius: 0.5, bgcolor: colors.muted }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: 24, pl: 0.25 }}>
                <Box sx={{ width: 28, height: 11, borderRadius: 0.5, bgcolor: colors.muted }} />
                <Box sx={{ width: 16, height: 11, borderRadius: 0.5, bgcolor: colors.muted }} />
                <Box sx={{ width: 48, height: 11, borderRadius: 0.5, bgcolor: colors.fill }} />
                <Box sx={{ width: 16, height: 11, borderRadius: 0.5, bgcolor: colors.muted }} />
                <Box sx={{ width: 48, height: 11, borderRadius: 0.5, bgcolor: colors.fill }} />
            </Box>
        </Box>
    )
}

type Props = {
    showMetadata?: boolean
}

const OiChartSkeletonOverlay: React.FC<Props> = ({ showMetadata = true }) => (
    <>
        <Box sx={CHART_SX}>
            <OiChartSkeletonPlaceholder />
        </Box>
        {showMetadata ? (
            <Box sx={METADATA_OVERLAY_SX}>
                <OiMetadataSkeletonPlaceholder />
            </Box>
        ) : null}
    </>
)

export default memo(OiChartSkeletonOverlay)
